import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { readRwgAttribution } from "@/lib/rwg-attribution";
import { useSelfScheduleStore } from "@/stores/self-schedule-store";

/**
 * Hook to capture Google Reserve with Google (RWG) token from URL parameters.
 *
 * When a user clicks "Book Now" on Google Search/Maps, they're redirected with:
 * - rwg_token: Token for conversion tracking (30-day attribution window)
 * - location_id: The merchant/location they clicked on
 *
 * On mount, we also rehydrate from the 30-day rwg-attribution store so a
 * patient who returns to complete a booking after the self-schedule store's
 * 12-hour idle reset still gets attributed to Google (per Google's spec:
 * "store the token for 30 days after the initial referral").
 */
export function useRwgToken() {
  const $ = _c(10);
  const searchParams = useSearchParams();
  const {
    setRwgToken,
    setRwgMerchantId,
    rwgToken,
    rwgMerchantId
  } = useSelfScheduleStore();
  let t0;
  let t1;
  if ($[0] !== rwgMerchantId || $[1] !== rwgToken || $[2] !== searchParams || $[3] !== setRwgMerchantId || $[4] !== setRwgToken) {
    t0 = () => {
      if (!rwgToken || !rwgMerchantId) {
        const persisted = readRwgAttribution();
        const hydrate = {};
        if (persisted.token && !rwgToken) {
          hydrate.rwgToken = persisted.token;
        }
        if (persisted.merchantId && !rwgMerchantId) {
          hydrate.rwgMerchantId = persisted.merchantId;
        }
        if (Object.keys(hydrate).length > 0) {
          useSelfScheduleStore.setState(hydrate);
        }
      }
      const token = searchParams.get("rwg_token");
      const merchantId = searchParams.get("location_id");
      if (token) {
        setRwgToken(token);
      }
      if (merchantId) {
        setRwgMerchantId(merchantId);
      }
    };
    t1 = [searchParams, setRwgToken, setRwgMerchantId, rwgToken, rwgMerchantId];
    $[0] = rwgMerchantId;
    $[1] = rwgToken;
    $[2] = searchParams;
    $[3] = setRwgMerchantId;
    $[4] = setRwgToken;
    $[5] = t0;
    $[6] = t1;
  } else {
    t0 = $[5];
    t1 = $[6];
  }
  useEffect(t0, t1);
  let t2;
  if ($[7] !== rwgMerchantId || $[8] !== rwgToken) {
    t2 = {
      rwgToken,
      rwgMerchantId
    };
    $[7] = rwgMerchantId;
    $[8] = rwgToken;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}